import PropTypes from 'prop-types';
import { ReactComponent as Download } from 'assets/icons/svg/Download.svg';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from 'xlsx';

// material-ui
import FilterListIcon from '@mui/icons-material/FilterList';

import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
// assets
import DeleteIcon from '@mui/icons-material/Delete';
import { shouldForwardProp } from '@mui/system';
import { IconSearch } from '@tabler/icons-react';
import getAxiosInstance from 'api';
import { useEffect, useState } from 'react';
import UserAccessForm from './newUserForm';
import toast from 'react-hot-toast';
import ExcelUploadDialog from './excelUploadDialog';
const rejectionReasonMap = {
  'pdf is unsupported. kindly upload a new pdf.':
    'New Format',

  'pdf is an image. kindly upload a new pdf.':
    'Image Based',

  'pdf is password-protected. kindly upload a new pdf.':
    'Password Protected',

  'socket hang up':
    'Kindly Re-upload Bank Statement After 5 minutes',

  'cannot convert float nan to integer':
    'Bank statement is processing',

  'connect econnrefused':
    'Kindly Re-upload Bank Statement After 5 minutes or check back after 12 hours',

  'an unexpected error occurred':
    'Bank Statement is processing you will be notified',

  'bank statement is >3 years':
    'Bank Statement More than >3 years. Please upload Bank statement within <3 years',

  'error while uploading transactions':
    'Kindly Re-upload Bank Statement After 5 minutes or check back after 12 hours',

  'internal server error':
    'Bank statement is processing',

  'consent expired':
    'Consent has expired. Please try again or create a new consent request.',
};

const getRejectionReasonLabel = (reason) => {
  if (!reason) return '';

  const normalizedReason = reason.trim().toLowerCase();

  for (const [errorText, customerMessage] of Object.entries(
    rejectionReasonMap
  )) {
    if (normalizedReason.includes(errorText.toLowerCase())) {
      return customerMessage;
    }
  }

  return reason;
};
const colorChip = {
  Created: {
    color: '#FF8743',
    background: '#FFF3EC',
    title: '#FFF3EC',
  },
  'In Progress': {
    color: '#FFDD14',
    background: '#FFFEE0',
  },
  Active: {
    color: '#1CB110',
    background: '#E8F7E7',
  },
  InActive: {
    color: '#1CB110',
    background: '#E8F7E7',
  },
  Verified: {
    color: '#1CB110',
    background: '#E8F7E7',
  },
  Failed: {
    color: '#F44336',
    background: '#FDECEA',
  },
  Cancelled: {
    color: '#F44336',
    background: '#FDECEA',
  },
  Deleted: {
    color: '#F44336',
    background: '#FDECEA',
  },
};

// table filter
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

// table header
const headCells = [
  {
    id: 'referenceId',
    numeric: false,
    disablePadding: false,
    label: '',
  },
  {
    id: '',
    numeric: false,
    disablePadding: false,
    label: 'Reference ID',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email Address',
  },
  {
    id: 'firstName',
    numeric: true,
    disablePadding: false,
    label: 'First Name',
  },
  {
    id: 'lastName',
    numeric: true,
    disablePadding: false,
    label: 'Last Name',
  },
  {
    id: 'state',
    numeric: true,
    disablePadding: false,
    label: 'State',
  },
  {
    id: 'fundType',
    numeric: true,
    disablePadding: false,
    label: 'Fund Type',
  },
  {
    id: 'bank',
    numeric: true,
    disablePadding: false,
    label: 'Bank Name',
  },
  {
    id: 'companyName',
    label: 'Company',
  },
  {
    id: 'accountName',
    numeric: true,
    disablePadding: false,
    label: 'Bank Name',
  },
  {
    id: 'accountNumber',
    numeric: true,
    disablePadding: false,
    label: 'Account Number',
  },
  {
    id: 'uploadType',
    numeric: true,
    disablePadding: false,
    label: 'M/D Upload',
  },
  {
    id: 'uploadTime',
    numeric: true,
    disablePadding: false,
    label: 'Upload Time',
  },
  {
    id: 'timeStamp',
    numeric: true,
    disablePadding: false,
    label: 'Completed Date',
  },
  {
    id: 'status',
    numeric: true,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'action',
    numeric: true,
    disablePadding: false,
    label: 'Download',
  },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '500',
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(
  ({ theme }) => ({
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: '8px',
    border: '1px solid rgba(33, 33, 32, 0.10)',
    background: '#FFFFFF',

    '& input': {
      background: 'transparent !important',
      paddingLeft: '4px !important',
    },
    [theme.breakpoints.down('lg')]: {
      width: 250,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 4,
      background:
        theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff',
    },
  })
);

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#F0F0F0',
    color: 'rgba(33, 33, 32, 0.60)',
    border: '1px solid #DEDEDE',
  }));

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCellHead
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCellHead>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

// ==============================|| TABLE - TOOLBAR ||============================== //

const EnhancedTableToolbar = ({ numSelected }) => (
  <Toolbar
    sx={{
      p: 0,
      pl: 1,
      pr: 1,
      ...(numSelected > 0 && {
        color: (theme) => theme.palette.secondary.main,
      }),
    }}
  >
    {numSelected > 0 ? (
      <Typography color="inherit" variant="subtitle1">
        {numSelected} selected
      </Typography>
    ) : (
      <Typography variant="h4" id="tableTitle">
        Nutrition
      </Typography>
    )}
    <Box sx={{ flexGrow: 1 }} />
    {numSelected > 0 && (
      <Tooltip title="Delete">
        <IconButton size="large">
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    )}
  </Toolbar>
);

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

// ==============================|| TABLE - ENHANCED ||============================== //

export default function BOIReportHistory({ appName }) {
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem('userData'));
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('uploadTime');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [apiList, setApiList] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState();
  const [statusFilter, setStatusFilter] = useState();
  const [anchorEll, setAnchorEll] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const open = Boolean(anchorEll);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const [selectedConsentId, setSelectedConsentId] = useState();
  const [selectedAccountNumber, setSelectedAccountNumber] = useState();
  const [selectedUserId, setSelectedUserId] = useState();
  const [selectedRow, setSelectedRow] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [metricLoading, setMetricLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [confirmType, setConfirmType] = useState();
  const [confirmOpen, setConfirmOpen] = useState();
  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return '-';

    const acc = String(accountNumber);

    if (acc.length <= 4) return acc;

    return '*'.repeat(acc.length - 4) + acc.slice(-4);
  };
  const handleDropClick = (event) => {
    setAnchorEll(event.currentTarget);
  };

  const handleDropClose = () => {
    setAnchorEll(null);
  };

  const handleStatusMenuClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPage(0); // Reset to first page when status filter changes
    handleStatusMenuClose();
  };

  useEffect(() => {
    fetchRoleAndState();
  }, []);

  const fetchRoleAndState = async () => {
    try {
      const res = await getAxiosInstance(
        'businessPartner/getStatesAndRoles',
        'get'
      );
      if (res?.data?.codes === 200) {
        const states = res?.data?.data?.states;
        const roles = res?.data?.data?.roles;
        setStates(states || []);
        setRoles(roles || []);
      } else {
        toast.error('Failed to fetch roles and states');
      }
    } catch (err) {
      toast.error(
        err?.message || 'Something went wrong while fetching roles and states'
      );
    }
  };

  const fetchBoiReportHistory = () => {
    setLoading(true);
    const payload = {
      sortBy: orderBy,
      sortDirection: order,
      filter: {
        state: filter === 'All' ? undefined : filter,
        status: statusFilter === 'All' ? undefined : statusFilter,
      },
      'journey-type': 'BOI',
    };
    getAxiosInstance(
      'businessPartner/getExternalConsentByBusiness',
      'post',
      payload
    ).then((res) => {
      if (res?.data?.codes === 200) {
        setLoading(false);
        setApiList(
          Array.isArray(res?.data?.data?.items) ? res?.data?.data?.items : []
        );
        setTotal(
          Array.isArray(res?.data?.data?.items)
            ? res?.data?.data?.items.length
            : 0
        );
      }
    });
  };

  useEffect(() => {
    fetchBoiReportHistory();
  }, [orderBy, order, filter, statusFilter]);

  const handleMenuClick = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStateFilterChange = (state) => {
    setFilter(state);
    setPage(0); // Reset to first page when state filter changes
    handleClose();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };
  const isSelected = () => {};

  const handleClick = (event, row) => {
    const selectedIndex = selected.indexOf(row.name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row.name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };
  const downloadFileFromBuffer = (
    byteArray,
    filename,
    mimeType = 'application/octet-stream'
  ) => {
    const blob = new Blob([new Uint8Array(byteArray)], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 when rows per page changes
  };

  // Frontend search filter function
  const filterDataBySearch = (data) => {
    if (!search || String(search).trim() === '') {
      return data;
    }
    const searchTerm = String(search).toLowerCase().trim();
    return data.filter((row) => {
      const email = String(row?.email || '').toLowerCase();
      const firstName = String(row?.firstName || '').toLowerCase();
      const lastName = String(row?.lastName || '').toLowerCase();
      const referenceId = String(row?.referenceId || '').toLowerCase();
      
      return (
        email.includes(searchTerm) ||
        firstName.includes(searchTerm) ||
        lastName.includes(searchTerm) ||
        referenceId.includes(searchTerm)
      );
    });
  };

  // Handle search change - reset page to 0
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0); // Reset to first page when search changes
  };

  // Get filtered data
  const filteredData = filterDataBySearch(apiList);
  const filteredTotal = filteredData.length;
  const handleViewStatementPdf = async () => {
    const statementPath = selectedRow?.statementPath
      ? String(selectedRow.statementPath).trim()
      : '';

    if (statementPath) {
      try {
        const res = await getAxiosInstance(
          'businessPartner/getPdfSignedUrl',
          'post',
          { path: statementPath }
        );
        if (res?.data?.codes === 200) {
          const pdfUrl = res?.data?.data?.pdfUrl;
          if (pdfUrl) {
            window.open(pdfUrl, '_blank');
            handleDropClose();
          } else {
            toast.error('PDF link not found.');
          }
        } else {
          toast.error(res?.data?.message || 'Failed to get PDF URL.');
        }
      } catch (err) {
        toast.error(err?.message || 'Error fetching PDF URL.');
      }
      return;
    }

    const pdfLink = selectedRow?.pdfLink ? String(selectedRow.pdfLink).trim() : '';
    if (pdfLink) {
      window.open(pdfLink, '_blank');
      handleDropClose();
    }
  };

  const handleDownloadStatement = () => {
    getAxiosInstance(
      'businessPartner/getCSVStatementUrlForBOI',
      'post',
      {
        accountNumber: selectedAccountNumber,
        monthYear: moment().format('YYYY-MM'),
        userId: selectedUserId,
        consentId: selectedConsentId,
      },
      {
        'journey-type': 'BOI',
      }
    )
      .then((res) => {
        const url = res?.data?.data?.data?.statementUrl?.url;
        if (res?.data?.codes === 200 && url) {
          window.open(url, '_blank');
        } else {
          console.warn('Statement URL not found.');
        }
      })
      .catch((err) => {
        console.error('Error fetching statement', err);
      });
  };
  const handleDownloadMetrics = () => {
    setMetricLoading(true);

    getAxiosInstance(
      'orchestrator/consent/getBOIMetrics',
      'post',
      { consentId: selectedConsentId },
      { 'journey-type': 'BOI' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const bufferData = res.data.data.pdfBuffer?.data;
          if (!bufferData || !bufferData.length) {
            toast.error('No PDF data received');
            setMetricLoading(false);
            return;
          }
          let filename = res.data.data.filename || 'downloaded-file.pdf';
          filename = String(filename); // Ensure filename is a string
          if (!filename.toLowerCase().endsWith('.pdf')) filename += '.pdf';

          const byteArray = new Uint8Array(bufferData);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          handleDropClose();
          setTimeout(() => {
            setMetricLoading(false);
          }, 1000);
        }
      })
      .catch((err) => {
        setMetricLoading(false);
        toast.error('Error while downloading metrics');
        console.error('Error downloading metrics:', err);
      });
  };

  const handleNewUser = () => {};

  const formatDataForExport = (data) => {
    return data.map((item) => ({
      'Reference ID': item.referenceId || '',
      'Email Address': item.email || '',
      'First Name': item.firstName || '',
      'Last Name': item.lastName || '',
      State: item.state || '',
      'Fund Type': item.fundType || '',
      'Bank Name': item.bank || '',
      'Company': item.companyName || '',
      'Bank Name ': item.accountName || '',
      'Account Number': maskAccountNumber(item.accountNumber),
      'M/D Upload': item.uploadType || '',
      'Upload Time': item.uploadTime
        ? moment(item.uploadTime).format('MMM DD, YYYY HH:mm A')
        : '',
      'Completed Date': item.timeStamp
        ? moment(item.timeStamp).format('MMM DD, YYYY HH:mm A')
        : '',
      Status: item.status || '',
    }));
  };
  const handleToggleRow = (consentId, event) => {
    let prevSelected = selected;
    if (event.target.checked) {
      prevSelected = prevSelected.includes(consentId)
        ? prevSelected
        : [...prevSelected, consentId];
    } else {
      prevSelected = prevSelected.filter((id) => id !== consentId);
    }
    setSelected(prevSelected);
  };

  const handleConfirm = () => {
    if (confirmType === 'selected') {
      onClearSelected();
    } else if (confirmType === 'all') {
      onClearAll();
    }
    setConfirmOpen(false);
    setConfirmType(null);
  };
  const onClearSelected = async () => {
    if (selected.length === 0) return;
    try {
      const payload = {
        consentIds: selected,
        deleteAll: false,
      };
      const res = await getAxiosInstance(
        `businessPartner/deleteExternalConsents`,
        'post',
        payload,
        {
          'journey-type': 'App-Register',
        }
      );
      if (res?.data?.codes === 200) {
        toast.success('Selected consents cleared successfully');
        setSelected([]);
        fetchBoiReportHistory();
      } else {
        toast.error(res?.data?.message || 'Failed to clear selected consents');
      }
    } catch (err) {
      toast.error(err?.message || 'Error clearing selected consents');
    }
  };

  const onClearAll = async () => {
    try {
      const payload = {
        deleteAll: true,
        consentIds: [],
      };
      const res = await getAxiosInstance(
        `businessPartner/deleteExternalConsents`,
        'post',
        payload,
        {
          'journey-type': 'App-Register',
        }
      );

      if (res?.data?.codes === 200) {
        toast.success('All consents cleared successfully');
        setSelected([]);
        fetchBoiReportHistory();
      } else {
        toast.error(res?.data?.message || 'Failed to clear all consents');
      }
    } catch (err) {
      toast.error(err?.message || 'Error clearing all consents');
    }
  };

  const handleExport = () => {
    setExportLoading(true);

    const payload = {
      sortBy: orderBy,
      sortDirection: order,
      filter: {
        status: statusFilter === 'All' ? undefined : statusFilter,
      },
    };

    getAxiosInstance(
      'businessPartner/getExternalConsentByBusiness',
      'post',
      payload,
      {
        'journey-type': 'BOI',
      }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const allItems = Array.isArray(res?.data?.data?.items)
            ? res?.data?.data?.items
            : [];
          const formattedData = formatDataForExport(allItems);

          // Create workbook and worksheet
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(formattedData);

          // Set column widths
          const colWidths = [
            { wch: 15 }, // Reference ID
            { wch: 25 }, // Email Address
            { wch: 15 }, // First Name
            { wch: 15 }, // Last Name
            { wch: 10 }, // State
            { wch: 15 }, // Fund Type
            { wch: 20 }, // Bank Name
            { wch: 20 }, // Account Number
            { wch: 15 }, // M/D Upload
            { wch: 20 }, // Upload Time
            { wch: 20 }, // Completed Date
            { wch: 15 }, // Status
          ];
          ws['!cols'] = colWidths;

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(wb, ws, 'BOI Report History');

          // Generate Excel file
          const excelBuffer = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'array',
          });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          // Download file
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'BOI_Report_History.xlsx';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();

          setExportLoading(false);
          toast.success('Export completed successfully');
        }
      })
      .catch((err) => {
        setExportLoading(false);
        toast.error(err?.message || 'Error exporting data');
      });
  };

  return (
    <Box mt={3}>
      <Stack
        direction={'row'}
        flexWrap={'wrap'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
        >
          <Typography variant="h3">Dashboard</Typography>
          <OutlineInputStyle
            id="input-search-header"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by email, first name, last name, or reference ID"
            sx={{
              border: 'none',
            }}
            startAdornment={
              <InputAdornment position="start">
                <IconSearch
                  stroke={1.5}
                  size="16px"
                  color={theme.palette.grey[500]}
                />
              </InputAdornment>
            }
            aria-describedby="search-helper-text"
            inputProps={{ 'aria-label': 'search' }}
          />
          {userData?.userAccessRole === 'Primary' ? (
            <>
              <Button
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid rgba(33, 33, 32, 0.10)',
                  color: '#212120',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '22px',
                  // padding: '12px',
                }}
                variant="outlined"
                onClick={(e) => {
                  handleMenuClick(e);
                }}
                size="small"
                startIcon={<FilterListIcon sx={{ color: 'grey.500' }} />}
              >
                {filter ? `State: ${filter}` : 'Filter State'}
              </Button>
              <Menu
                id="menu-popular-card-1"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                variant="selectedMenu"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiMenu-paper': {
                    boxShadow: theme.customShadows.z1,
                  },
                }}
              >
                {['All', ...states]?.map((item) => (
                  <MenuItem
                    key={item}
                    onClick={() => handleStateFilterChange(item)}
                  >
                    {' '}
                    {item}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid rgba(33, 33, 32, 0.10)',
                  color: '#212120',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '22px',
                  // padding: '12px',
                }}
                variant="outlined"
                onClick={(e) => {
                  handleStatusMenuClick(e);
                }}
                size="small"
                startIcon={<FilterListIcon sx={{ color: 'grey.500' }} />}
              >
                {statusFilter ? `Status: ${statusFilter}` : 'Filter Status'}
              </Button>
              <Menu
                id="menu-status-filter"
                anchorEl={statusAnchorEl}
                keepMounted
                open={statusMenuOpen}
                onClose={handleStatusMenuClose}
                variant="selectedMenu"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiMenu-paper': {
                    boxShadow: theme.customShadows.z1,
                  },
                }}
              >
                {['All', 'Active', 'Failed', 'In Progress', 'Created']?.map(
                  (item) => (
                    <MenuItem
                      key={item}
                      onClick={() => handleStatusFilterChange(item)}
                    >
                      {' '}
                      {item}
                    </MenuItem>
                  )
                )}
              </Menu>
            </>
          ) : (
            <></>
          )}
        </Stack>
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
        >
          <Button
            color="secondary"
            variant="outlined"
            onClick={handleExport}
            disabled={exportLoading}
            startIcon={
              exportLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {exportLoading ? 'Exporting...' : 'Export'}
          </Button>
          {userData?.userAccessRole === 'Primary' ? (
            <>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => setUploadOpen(true)}
              >
                Upload
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => setIsOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 20 }} />}
              >
                Assign role and Permissions
              </Button>
            </>
          ) : (
            <></>
          )}
        </Stack>
      </Stack>
      <Stack
        direction="row"
        gap={2}
        mt={2}
        flexWrap="wrap"
        alignItems={'baseline'}
      >
        <Button
          variant="outlined"
          color="error"
          disabled={selected.length === 0}
          onClick={() => {
            setConfirmType('selected');
            setConfirmOpen(true);
          }}
        >
          Clear Selected
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={filteredData.length === 0}
          onClick={() => {
            setConfirmType('all');
            setConfirmOpen(true);
          }}
        >
          Clear All
        </Button>
        <Typography fontWeight={600}>{selected.length} selected</Typography>
      </Stack>
      <Card sx={{ mt: 2 }}>
        <>
          {/* table */}
          <TableContainer>
            <Table
              sx={{
                minWidth: 750,
                backgroundColor: 'white',
              }}
              aria-labelledby="tableTitle"
              size={loading ? 'small' : 'medium'}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={filteredTotal}
              />
              <TableBody>
                {loading ? (
                  <TableRow>
                    <StyledTableCell colSpan={12} align="center">
                      <CircularProgress color="secondary" />
                    </StyledTableCell>
                  </TableRow>
                ) : filteredData.length ? (
                  <>
                    {filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        if (typeof row === 'number') return null;
                        const isItemSelected = isSelected(row.name);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => handleClick(event, row)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.name}
                            selected={isItemSelected}
                          >
                            <StyledTableCell>
                              <Checkbox
                                color="secondary"
                                disabled={!row?.consentId} // disable checkbox for invalid rows
                                checked={
                                  !!row?.consentId &&
                                  selected.includes(row.consentId)
                                }
                                onChange={(e) => {
                                  if (row?.consentId)
                                    handleToggleRow(row.consentId, e);
                                }}
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.referenceId}
                            </StyledTableCell>
                            <StyledTableCell>{row?.email}</StyledTableCell>
                            <StyledTableCell>{row.firstName}</StyledTableCell>
                            <StyledTableCell>{row.lastName}</StyledTableCell>
                            <StyledTableCell>{row.state}</StyledTableCell>

                            <StyledTableCell>{row.fundType}</StyledTableCell>
                            <StyledTableCell>{row.bank}</StyledTableCell>

                            <StyledTableCell>{row.companyName || '-'}</StyledTableCell>

                            <StyledTableCell>{row.accountName || '-'}</StyledTableCell>
                            <StyledTableCell>
                              {row.accountNumber
                                ? maskAccountNumber(row.accountNumber)
                                : '-'}
                            </StyledTableCell>
                            <StyledTableCell>{row.uploadType}</StyledTableCell>
                            <StyledTableCell>
                              {moment(row.uploadTime).format(
                                'MMM DD, YYYY HH:mm A'
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              {moment(row.timeStamp).format(
                                'MMM DD, YYYY HH:mm A'
                              )}
                            </StyledTableCell>

                            <StyledTableCell>
                              <Chip
                                size="small"
                                label={row.rejectionReason ? (
                                  <span>
                                    {row.status}
                                    <br />
                                    {getRejectionReasonLabel(row.rejectionReason)}
                                  </span>
                                ) : row.status}
                                sx={{
                                  borderRadius: '4px',
                                  color: colorChip[row.status]?.color,
                                  background: colorChip[row.status]?.background,
                                  textTransform: 'capitalize',
                                  whiteSpace: 'pre-line',
                                  minHeight: row.rejectionReason ? '40px' : '32px',
                                  height: 'auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingTop: row.rejectionReason ? '6px' : '0',
                                  paddingBottom: row.rejectionReason ? '6px' : '0',
                                }}
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Box position="relative">
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  fontSize={16}
                                  color={'#0DACF5'}
                                  sx={{
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    opacity: 1,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedConsentId(row.consentId);
                                    setSelectedAccountNumber(row.accountNumber);
                                    setSelectedUserId(row.userId);
                                    setSelectedRow(row);
                                    handleDropClick(e);
                                  }}
                                >
                                  <Download />
                                  <Typography>Download</Typography>
                                </Box>

                                <Menu
                                  anchorEl={anchorEll}
                                  open={open}
                                  onClose={handleDropClose}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                  }}
                                >
                                  <MenuItem
                                    onClick={handleViewStatementPdf}
                                    disabled={
                                      !(
                                        (selectedRow?.statementPath &&
                                          String(selectedRow.statementPath).trim()) ||
                                        (selectedRow?.pdfLink &&
                                          String(selectedRow.pdfLink).trim())
                                      )
                                    }
                                  >
                                    View Statement PDF
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => handleDownloadStatement(row)}
                                    disabled={selectedRow?.status !== 'Active'}
                                  >
                                    Download Statement
                                  </MenuItem>
                                  <MenuItem
                                    onClick={handleDownloadMetrics}
                                    disabled={selectedRow?.status !== 'Active'}
                                  >
                                    {metricLoading ? (
                                      <CircularProgress size={22} />
                                    ) : (
                                      'Download Metrics'
                                    )}
                                  </MenuItem>
                                </Menu>
                              </Box>
                            </StyledTableCell>
                          </TableRow>
                        );
                      })}
                  </>
                ) : (
                  <>
                    {loading ? (
                      <TableRow>
                        <StyledTableCell colSpan={12} align="center">
                          <CircularProgress color="secondary" />
                        </StyledTableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <StyledTableCell colSpan={12} align="center">
                          No data available
                        </StyledTableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            align="center"
          >
            <DialogTitle>
              <Typography fontSize={24} fontWeight={600}>
                Confirm
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography fontSize={16} fontWeight={500}>
                Are you sure
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleConfirm}>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* table pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTotal}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          <UserAccessForm
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            fetchBoiReportHistory={fetchBoiReportHistory}
            roles={roles}
            states={states}
          />

          <ExcelUploadDialog
            isOpen={uploadOpen}
            setIsOpen={setUploadOpen}
            fetchBoiReportHistory={fetchBoiReportHistory}
          />
        </>
      </Card>
    </Box>
  );
}
